<template>
  <!-- Fullscreen Variant -->
  <div
    v-if="variant === 'fullscreen' && isVisible"
    class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/30 dark:bg-black/70 backdrop-blur-md transition-all duration-300"
    role="status"
    aria-live="polite"
  >
    <div
      class="relative w-full max-w-sm mx-auto bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-2xl rounded-3xl p-7 sm:p-8 text-center shadow-[0_20px_60px_-15px_rgba(0,0,0,0.2)] dark:shadow-[0_25px_70px_-15px_rgba(0,0,0,0.7)] border border-slate-200/80 dark:border-[#263449]/80 overflow-hidden"
    >
      <!-- Top subtle ambient highlight aura -->
      <div
        class="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full bg-gradient-to-b from-indigo-500/20 via-blue-500/10 to-transparent blur-2xl pointer-events-none"
      ></div>

      <!-- Core Animated Visual -->
      <div class="relative w-20 h-20 mx-auto mb-5 flex items-center justify-center">
        <!-- Ambient pulsating glow circle -->
        <div
          class="absolute inset-0 rounded-full bg-gradient-to-tr from-indigo-500/20 to-blue-500/20 blur-md animate-pulse-glow pointer-events-none"
        ></div>

        <!-- SVG Orbital Gradient Arc -->
        <svg
          class="w-20 h-20 animate-orbital relative z-10"
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="orbital-grad-indigo" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#6366F1" stop-opacity="0.1" />
              <stop offset="40%" stop-color="#4F46E5" />
              <stop offset="100%" stop-color="#06B6D4" />
            </linearGradient>
            <linearGradient id="orbital-grad-blue" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#3B82F6" stop-opacity="0.1" />
              <stop offset="50%" stop-color="#2563EB" />
              <stop offset="100%" stop-color="#38BDF8" />
            </linearGradient>
            <linearGradient id="orbital-grad-emerald" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#10B981" stop-opacity="0.1" />
              <stop offset="50%" stop-color="#059669" />
              <stop offset="100%" stop-color="#34D399" />
            </linearGradient>
          </defs>

          <!-- Outer track -->
          <circle
            cx="40"
            cy="40"
            r="34"
            stroke="currentColor"
            stroke-width="3"
            class="text-slate-100 dark:text-slate-800/80"
          />

          <!-- Active gradient arc with rounded caps -->
          <circle
            cx="40"
            cy="40"
            r="34"
            :stroke="`url(#orbital-grad-${gradientId})`"
            stroke-width="3.5"
            stroke-linecap="round"
            stroke-dasharray="160"
            stroke-dashoffset="65"
          />
        </svg>

        <!-- Center Counter-Orbital Satellite Dot -->
        <div
          class="absolute inset-2 flex items-center justify-center animate-counter-spin pointer-events-none"
        >
          <div
            class="w-1.5 h-1.5 rounded-full bg-indigo-500 dark:bg-cyan-400 shadow-[0_0_8px_rgba(99,102,241,0.8)] -translate-y-[28px]"
          ></div>
        </div>

        <!-- Center Floating Glass Squircle with Icon -->
        <div
          class="absolute inset-0 m-auto w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-50 dark:from-indigo-950/40 to-blue-50 dark:to-blue-950/30 border border-indigo-100 dark:border-indigo-800/40 flex items-center justify-center shadow-xs z-10"
        >
          <slot name="icon">
            <!-- Default Cap / Brand Icon -->
            <svg
              v-if="icon === 'cap'"
              class="w-5 h-5 text-indigo-600 dark:text-indigo-400 transition-transform duration-700"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
              <path d="M6 12v5c3 3 9 3 12 0v-5" />
            </svg>
            <svg
              v-else-if="icon === 'sparkle'"
              class="w-5 h-5 text-indigo-600 dark:text-indigo-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            </svg>
            <svg
              v-else
              class="w-5 h-5 text-indigo-600 dark:text-indigo-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </svg>
          </slot>
        </div>
      </div>

      <!-- Text Content -->
      <div class="space-y-1.5 mb-5">
        <h3
          class="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100 tracking-tight"
        >
          {{ message }}
        </h3>
        <p class="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
          {{ subtext }}
        </p>
      </div>

      <!-- Fluid Shimmer Progress Track -->
      <div
        v-if="showProgress"
        class="w-full bg-slate-100 dark:bg-slate-800/80 rounded-full h-1.5 overflow-hidden relative"
      >
        <div
          class="h-full rounded-full bg-gradient-to-r from-transparent via-indigo-500 to-transparent dark:via-cyan-400 animate-bar-progress"
        ></div>
      </div>

      <!-- Synchronized Staggered Status Dots -->
      <div class="flex items-center justify-center gap-1.5 mt-4">
        <span
          class="w-1.5 h-1.5 rounded-full bg-indigo-600 dark:bg-indigo-400 animate-ping opacity-75"
          style="animation-duration: 1.6s;"
        ></span>
        <span
          class="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 animate-ping opacity-75"
          style="animation-duration: 1.6s; animation-delay: 0.3s;"
        ></span>
        <span
          class="w-1.5 h-1.5 rounded-full bg-cyan-500 dark:bg-cyan-400 animate-ping opacity-75"
          style="animation-duration: 1.6s; animation-delay: 0.6s;"
        ></span>
      </div>
    </div>
  </div>

  <!-- Card / Section Overlay Variant -->
  <div
    v-else-if="variant === 'card' && isVisible"
    class="absolute inset-0 z-20 flex flex-col items-center justify-center p-6 bg-white/85 dark:bg-[#111C30]/90 backdrop-blur-xs rounded-2xl transition-all"
    role="status"
  >
    <div class="relative w-14 h-14 mb-3 flex items-center justify-center">
      <div
        class="absolute inset-0 rounded-full bg-indigo-500/10 blur-sm animate-pulse-glow pointer-events-none"
      ></div>
      <svg
        class="w-14 h-14 animate-orbital"
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="28"
          cy="28"
          r="23"
          stroke="currentColor"
          stroke-width="2.5"
          class="text-slate-100 dark:text-slate-800"
        />
        <circle
          cx="28"
          cy="28"
          r="23"
          stroke="url(#card-grad)"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-dasharray="110"
          stroke-dashoffset="45"
        />
        <defs>
          <linearGradient id="card-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#4F46E5" stop-opacity="0.1" />
            <stop offset="60%" stop-color="#4F46E5" />
            <stop offset="100%" stop-color="#06B6D4" />
          </linearGradient>
        </defs>
      </svg>
      <div
        class="absolute w-7 h-7 rounded-xl bg-indigo-50/80 dark:bg-indigo-950/50 flex items-center justify-center"
      >
        <div class="w-2 h-2 rounded-full bg-indigo-600 dark:bg-cyan-400 animate-pulse"></div>
      </div>
    </div>
    <p v-if="message" class="text-sm font-semibold text-slate-800 dark:text-slate-100 text-center">
      {{ message }}
    </p>
    <p v-if="subtext" class="text-xs text-slate-500 dark:text-slate-400 text-center mt-0.5">
      {{ subtext }}
    </p>
  </div>

  <!-- Inline / Container Variant -->
  <div
    v-else-if="variant === 'inline' && isVisible"
    class="flex items-center justify-center gap-3 py-4"
    :class="{ 'flex-col': layout === 'vertical' }"
    role="status"
  >
    <div
      class="relative flex items-center justify-center flex-shrink-0"
      :class="sizeContainerClasses"
    >
      <svg
        :class="['animate-orbital', sizeSvgClasses]"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="20"
          cy="20"
          r="16"
          stroke="currentColor"
          stroke-width="2.5"
          class="text-slate-100 dark:text-slate-800"
        />
        <circle
          cx="20"
          cy="20"
          r="16"
          :stroke="`url(#inline-grad-${color})`"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-dasharray="75"
          stroke-dashoffset="30"
        />
        <defs>
          <linearGradient :id="`inline-grad-${color}`" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" :stop-color="accentColor" stop-opacity="0.1" />
            <stop offset="70%" :stop-color="accentColor" />
            <stop offset="100%" :stop-color="secondaryColor" />
          </linearGradient>
        </defs>
      </svg>
      <div
        v-if="size === 'lg' || size === 'md'"
        class="absolute w-2 h-2 rounded-full bg-indigo-600 dark:bg-cyan-400 animate-ping opacity-60"
      ></div>
    </div>
    <div v-if="message || subtext" :class="{ 'text-center': layout === 'vertical' }">
      <p v-if="message" class="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200">
        {{ message }}
      </p>
      <p v-if="subtext" class="text-[11px] text-slate-500 dark:text-slate-400">
        {{ subtext }}
      </p>
    </div>
  </div>

  <!-- Micro / Button Variant -->
  <span
    v-else-if="variant === 'button' && isVisible"
    class="inline-flex items-center justify-center flex-shrink-0"
    role="status"
  >
    <svg
      class="w-4 h-4 animate-orbital"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        stroke-width="2.2"
        class="opacity-25"
      />
      <path
        d="M12 3a9 9 0 0 1 9 9"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
      />
    </svg>
  </span>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String,
    default: "inline", // 'fullscreen' | 'card' | 'inline' | 'button'
    validator: (v) => ["fullscreen", "card", "inline", "button"].includes(v),
  },
  size: {
    type: String,
    default: "md", // 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  },
  layout: {
    type: String,
    default: "horizontal", // 'horizontal' | 'vertical'
  },
  message: {
    type: String,
    default: "Loading...",
  },
  subtext: {
    type: String,
    default: "Please wait a moment while we process your request...",
  },
  color: {
    type: String,
    default: "indigo", // 'indigo' | 'blue' | 'emerald' | 'amber'
  },
  icon: {
    type: String,
    default: "cap", // 'cap' | 'sparkle' | 'layers'
  },
  showProgress: {
    type: Boolean,
    default: true,
  },
});

const gradientId = computed(() => {
  if (["indigo", "blue", "emerald"].includes(props.color)) {
    return props.color;
  }
  return "indigo";
});

const accentColor = computed(() => {
  switch (props.color) {
    case "blue":
      return "#2563EB";
    case "emerald":
      return "#059669";
    case "amber":
      return "#D97706";
    case "indigo":
    default:
      return "#4F46E5";
  }
});

const secondaryColor = computed(() => {
  switch (props.color) {
    case "blue":
      return "#38BDF8";
    case "emerald":
      return "#34D399";
    case "amber":
      return "#FBBF24";
    case "indigo":
    default:
      return "#06B6D4";
  }
});

const sizeContainerClasses = computed(() => {
  switch (props.size) {
    case "xs":
      return "w-4 h-4";
    case "sm":
      return "w-6 h-6";
    case "lg":
      return "w-12 h-12";
    case "xl":
      return "w-16 h-16";
    case "md":
    default:
      return "w-8 h-8";
  }
});

const sizeSvgClasses = computed(() => {
  switch (props.size) {
    case "xs":
      return "w-4 h-4";
    case "sm":
      return "w-6 h-6";
    case "lg":
      return "w-12 h-12";
    case "xl":
      return "w-16 h-16";
    case "md":
    default:
      return "w-8 h-8";
  }
});
</script>
